import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Loader2, Search } from 'lucide-react';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { problemsService, SnomedConditionRef } from '@/services/problemsService';

export interface DiagnosisPick {
  display: string;
  snomedCode: string;
  icd10?: string;
}

interface DiagnosisComboboxProps {
  value: DiagnosisPick | null;
  onChange: (next: DiagnosisPick | null) => void;
  disabled?: boolean;
  placeholder?: string;
  /** When true, treat free-text not in dictionary as a valid pick (no SNOMED). */
  allowFreeText?: boolean;
  className?: string;
}

const DEBOUNCE_MS = 180;

/**
 * Diagnosis typeahead — searches the curated SNOMED CT dictionary on the
 * backend. Selecting a result fills both display name and SNOMED code so the
 * practitioner doesn't have to memorize codes. Free-text fallback is supported
 * for diagnoses outside the dictionary.
 */
const DiagnosisCombobox: React.FC<DiagnosisComboboxProps> = ({
  value, onChange, disabled, placeholder = 'Search diagnoses…',
  allowFreeText = true, className,
}) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [debounced, setDebounced] = useState('');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setDebounced(input.trim()), DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [input]);

  const { data: results = [], isFetching } = useQuery<SnomedConditionRef[]>({
    queryKey: ['snomed-conditions', debounced],
    queryFn: () => problemsService.searchSnomedConditions(debounced, 25),
    staleTime: 5 * 60 * 1000, // dictionary is static — cache aggressively
    enabled: open,
  });

  const showFreeText = useMemo(() => {
    if (!allowFreeText || !input.trim()) return false;
    const exists = results.some(
      r => r.display.toLowerCase() === input.trim().toLowerCase(),
    );
    return !exists;
  }, [allowFreeText, input, results]);

  const buttonLabel = value ? value.display : placeholder;

  const handlePick = (pick: DiagnosisPick) => {
    onChange(pick);
    setOpen(false);
    setInput('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='outline'
          role='combobox'
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal',
            !value && 'text-slate-500',
            className,
          )}
        >
          <span className='flex items-center gap-2 min-w-0'>
            <Search className='h-3.5 w-3.5 shrink-0 text-slate-400' />
            <span className='truncate'>{buttonLabel}</span>
            {value?.snomedCode && (
              <Badge variant='outline' className='shrink-0 text-[10px]'>
                {value.snomedCode}
              </Badge>
            )}
          </span>
          <ChevronDown className='h-4 w-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align='start'
        className='w-[--radix-popover-trigger-width] min-w-[360px] p-0'
      >
        {/* `shouldFilter={false}` — server already ranked. */}
        <Command shouldFilter={false}>
          <CommandInput
            value={input}
            onValueChange={setInput}
            placeholder='Search by name, SNOMED, or ICD-10…'
          />
          <CommandList>
            {isFetching && results.length === 0 && (
              <div className='flex items-center justify-center py-6 text-sm text-slate-500'>
                <Loader2 className='mr-2 h-4 w-4 animate-spin' /> Searching…
              </div>
            )}
            {!isFetching && results.length === 0 && !showFreeText && (
              <CommandEmpty>No diagnoses match.</CommandEmpty>
            )}
            {results.length > 0 && (
              <CommandGroup heading={debounced ? 'Matches' : 'Common diagnoses'}>
                {results.map(r => (
                  <CommandItem
                    key={`${r.code}-${r.display}`}
                    value={`${r.code} ${r.display}`}
                    onSelect={() => handlePick({
                      display: r.display,
                      snomedCode: r.code,
                      icd10: r.icd10,
                    })}
                  >
                    <div className='flex flex-col gap-0.5 min-w-0 flex-1'>
                      <span className='truncate font-medium text-slate-900'>{r.display}</span>
                      <span className='text-[10px] text-slate-500'>
                        SNOMED {r.code}{r.icd10 ? ` · ICD-10 ${r.icd10}` : ''}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {showFreeText && (
              <CommandGroup heading='Free text'>
                <CommandItem
                  value={`__freetext_${input}`}
                  onSelect={() => handlePick({
                    display: input.trim(),
                    snomedCode: '',
                  })}
                >
                  <div className='flex flex-col gap-0.5'>
                    <span className='font-medium text-slate-900'>
                      Use “{input.trim()}” as free-text
                    </span>
                    <span className='text-[10px] text-slate-500'>
                      No SNOMED code — add manually if you have one
                    </span>
                  </div>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default DiagnosisCombobox;
